// DCIC-07 Facility Status Update (v2.2).
// Every record belongs to ONE facility (facility_uuid). update_type selects:
//   status_update      — R05 mobile mode: functional_status (+ GPS, notes)
//   climate_assessment — R04/R03 create, R02 approves: GHG baseline, emissions
//                        screening, CSA / waste-recovery / MRV verification
// An approved verification sets the read-only status shown on DCIC-06
// (practice_verification_status / approval_status) — see
// deriveFacilityClimateStatuses(). NLIMS records the assessor's
// classification; it never computes emissions.
//
// The backend has no facility-status-update / climate-assessment endpoint
// yet, and its facility update policy only allows edits while a facility is
// Draft or Rejected (never by R05). Until that endpoint exists, records are
// kept in browser storage with the same draft → submitted → validated →
// approved / rejected pipeline as every other instrument.
import { publishCollectionChange } from '../utils/collectionSync';
import { filterItemsByCurrentScope } from './viewerAccess';

const COLLECTION_KEY = 'facility-status-updates';
const STORAGE_KEY = 'nlims:facility-status-updates';

function readAll() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(rows) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    // Most likely the browser storage quota — usually a large report upload.
    throw new Error('Could not save this record in browser storage — the attached report may be too large. Try a smaller file.');
  }
}

function newUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `fsu-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function trimOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text || null;
}

const CLIMATE_FIELDS = [
  'assessment_type', 'assessment_scope', 'assessment_date', 'assessing_institution_uuid', 'assessing_institution_name',
  'linked_activity_uuid', 'linked_activity_title', 'verification_result', 'emissions_classification',
  'baseline_reference_uuid', 'assessment_report', 'assessment_report_file_name',
];
const STATUS_FIELDS = ['functional_status', 'gps_coordinates', 'notes'];

// Main fields (functional_status, gps_coordinates, notes) belong to every
// update; the [climate_assessment] fields are kept only for that update
// type, so a status update never carries stale assessment values.
function buildRecordFields(payload = {}) {
  const updateType = payload.update_type === 'climate_assessment' ? 'climate_assessment' : 'status_update';
  const out = {
    update_type: updateType,
    facility_uuid: trimOrNull(payload.facility_uuid),
    facility_name: trimOrNull(payload.facility_name),
    facility_type: trimOrNull(payload.facility_type),
    state: trimOrNull(payload.state),
    lga: trimOrNull(payload.lga),
  };
  STATUS_FIELDS.forEach((field) => { out[field] = trimOrNull(payload[field]); });
  CLIMATE_FIELDS.forEach((field) => { out[field] = updateType === 'climate_assessment' ? trimOrNull(payload[field]) : null; });
  if (updateType === 'climate_assessment') {
    const type = out.assessment_type;
    if (!['csa_verification', 'waste_recovery_verification', 'mrv_verification'].includes(type)) out.verification_result = null;
    if (type !== 'emissions_screening') { out.emissions_classification = null; out.baseline_reference_uuid = null; }
  }
  return out;
}

// assessment_outcome is pipeline-set, never keyed.
function outcomeFor(record) {
  if (record.update_type !== 'climate_assessment') return null;
  if (record.data_status === 'approved') return 'approved';
  if (record.data_status === 'rejected') return 'returned';
  return 'pending_approval';
}

function normalize(record = {}) {
  const dataStatus = String(record.data_status ?? 'draft').toLowerCase();
  const withStatus = { ...record, data_status: dataStatus };
  return {
    ...withStatus,
    id: record.uuid,
    dataStatus,
    assessment_outcome: outcomeFor(withStatus),
  };
}

function save(uuid, patch) {
  const rows = readAll();
  const index = rows.findIndex((row) => row.uuid === uuid);
  if (index === -1) throw new Error('Record not found.');
  const next = { ...rows[index], ...patch, updated_at: new Date().toISOString() };
  rows[index] = next;
  writeAll(rows);
  return normalize(next);
}

function transition(uuid, from, to, action, extra = {}) {
  const current = readAll().find((row) => row.uuid === uuid);
  if (!current) throw new Error('Record not found.');
  const status = String(current.data_status ?? 'draft').toLowerCase();
  if (!from.includes(status)) throw new Error(`Cannot ${action} a record that is ${status}.`);
  const item = save(uuid, { data_status: to, [`${to}_at`]: new Date().toISOString(), ...extra });
  publishCollectionChange(COLLECTION_KEY, { action, uuid });
  return item;
}

export const facilityStatusUpdatesApi = {
  collectionKey: COLLECTION_KEY,

  async listAll() {
    const items = filterItemsByCurrentScope(readAll().map(normalize))
      .sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')));
    return { items, pagination: { total: items.length } };
  },

  async get(uuid) {
    const row = readAll().find((r) => r.uuid === uuid);
    if (!row) throw new Error('Record not found.');
    return normalize(row);
  },

  async create(payload, user = {}) {
    const now = new Date().toISOString();
    const record = {
      uuid: newUuid(),
      ...buildRecordFields(payload),
      data_status: 'draft',
      created_by: user?.name ?? null,
      created_by_role: user?.roleId ?? null,
      created_at: now,
      updated_at: now,
    };
    writeAll([record, ...readAll()]);
    publishCollectionChange(COLLECTION_KEY, { action: 'create', uuid: record.uuid });
    return normalize(record);
  },

  async update(uuid, payload) {
    const current = readAll().find((row) => row.uuid === uuid);
    if (!current) throw new Error('Record not found.');
    if (!['draft', 'rejected'].includes(String(current.data_status).toLowerCase())) {
      throw new Error('Only draft or rejected records can be edited.');
    }
    const item = save(uuid, buildRecordFields(payload));
    publishCollectionChange(COLLECTION_KEY, { action: 'update', uuid });
    return item;
  },

  async remove(uuid) {
    writeAll(readAll().filter((row) => row.uuid !== uuid));
    publishCollectionChange(COLLECTION_KEY, { action: 'remove', uuid });
  },

  async submit(uuid) { return transition(uuid, ['draft', 'rejected'], 'submitted', 'submit', { rejection_notes: null }); },
  async validate(uuid) { return transition(uuid, ['submitted'], 'validated', 'validate'); },
  async approve(uuid) { return transition(uuid, ['validated'], 'approved', 'approve'); },
  async reject(uuid, notes = '') { return transition(uuid, ['submitted', 'validated'], 'rejected', 'reject', { rejection_notes: trimOrNull(notes) }); },
};

// Approved climate assessments, newest first — the lookup source for DCIC-07's
// baseline_reference and DCIC-12's facility_assessment_ref.
export function approvedClimateAssessments({ facilityUuid, assessmentType } = {}) {
  return readAll()
    .map(normalize)
    .filter((row) => row.update_type === 'climate_assessment' && row.data_status === 'approved')
    .filter((row) => !facilityUuid || row.facility_uuid === facilityUuid)
    .filter((row) => !assessmentType || row.assessment_type === assessmentType)
    .sort((a, b) => String(b.assessment_date ?? '').localeCompare(String(a.assessment_date ?? '')));
}

const VERIFICATION_TO_PRACTICE_STATUS = { verified: 'verified', partially_verified: 'pending', not_verified: 'rejected' };
const VERIFICATION_TO_APPROVAL_STATUS = { verified: 'approved', partially_verified: 'pending', not_verified: 'rejected' };

// System-set DCIC-06 statuses: written only by an approved DCIC-07 climate
// assessment (latest wins), never keyed on the facility form.
export function deriveFacilityClimateStatuses(facilityUuid) {
  if (!facilityUuid) return { practice_verification_status: 'pending', approval_status: 'pending' };
  const latest = (type) => approvedClimateAssessments({ facilityUuid, assessmentType: type })[0] ?? null;
  const csa = latest('csa_verification');
  const waste = latest('waste_recovery_verification');
  return {
    practice_verification_status: csa ? (VERIFICATION_TO_PRACTICE_STATUS[csa.verification_result] ?? 'pending') : 'pending',
    approval_status: waste ? (VERIFICATION_TO_APPROVAL_STATUS[waste.verification_result] ?? 'pending') : 'pending',
  };
}
